import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

/**
 * Subscribes to realtime changes on `decision_matrix_entries` for a given session.
 * Calls `onUpdate` so the parent can reload the matrix.
 */
export function useRealtimeMatrix(
  sessionId: string | undefined,
  currentUserId: string | null,
  onUpdate: () => void
) {
  const { toast } = useToast();
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;

  useEffect(() => {
    if (!sessionId) return;

    const channel = supabase
      .channel(`matrix_sync_${sessionId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "decision_matrix_entries",
          filter: `session_id=eq.${sessionId}`,
        },
        (payload) => {
          // We can't reliably filter by user since matrix entries don't have created_by.
          // Always refresh; toast only for non-trivial events.
          const eventType = payload.eventType;

          if (eventType === "INSERT" || eventType === "UPDATE") {
            toast({
              title: "Bewertung aktualisiert",
              description: "Die Decision Matrix wurde von einem Teammitglied geändert.",
            });
          }

          onUpdateRef.current();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [sessionId, currentUserId]);
}
